import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import payrollService from '../../../services/payrollService';
import NotificationToast from '../../../components/ui/NotificationToast';

const PaymentsBreakdownModal = ({ isOpen, onClose, payrollRecordId, employeeName, netSalary, onUpdate }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [filterType, setFilterType] = useState('all');

  const paymentTypes = [
    { value: 'salary', label: 'Salary' },
    { value: 'advance', label: 'Advance' },
    { value: 'bonus', label: 'Bonus' },
    { value: 'reimbursement', label: 'Reimbursement' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    if (isOpen && payrollRecordId) {
      loadPayments();
    }
  }, [isOpen, payrollRecordId]);

  const loadPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await payrollService.getPaymentsBreakdown(payrollRecordId);
      if (result.success) {
        setPayments(result.data || []);
      } else {
        setError(result.error || 'Failed to load payments');
      }
    } catch (err) {
      setError('Failed to load payments');
      console.error('Error loading payments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingPayment(null);
    setShowAddModal(true);
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setShowAddModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this payment entry?')) return;
    
    try {
      const result = await payrollService.deletePayment(id);
      if (result.success) {
        setNotification({ message: 'Payment entry deleted successfully', type: 'success' });
        loadPayments();
        onUpdate?.();
      } else {
        setNotification({ message: result.error || 'Failed to delete payment entry', type: 'error' });
      }
    } catch (err) {
      setNotification({ message: 'Failed to delete payment entry', type: 'error' });
    }
  };

  const handleSave = async (paymentData) => {
    try {
      let result;
      if (editingPayment) {
        result = await payrollService.updatePayment(editingPayment.id, paymentData);
      } else {
        result = await payrollService.createPayment({
          ...paymentData,
          payroll_record_id: payrollRecordId
        });
      }

      if (result.success) {
        setNotification({ 
          message: `Payment entry ${editingPayment ? 'updated' : 'created'} successfully`, 
          type: 'success' 
        });
        setShowAddModal(false);
        setEditingPayment(null);
        loadPayments();
        onUpdate?.();
      } else {
        setNotification({ message: result.error || 'Failed to save payment entry', type: 'error' });
      }
    } catch (err) {
      setNotification({ message: 'Failed to save payment entry', type: 'error' });
    }
  };

  const filteredPayments = filterType === 'all' 
    ? payments 
    : payments.filter(p => p.payment_type === filterType);

  const totalPaid = filteredPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const remaining = (netSalary || 0) - totalPaid;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div 
          className="bg-background rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Payment History</h2>
                <p className="text-sm text-muted-foreground mt-1">{employeeName}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={handleAdd} iconName="Plus" iconPosition="left">
                  Add Payment
                </Button>
                <Button variant="ghost" size="icon" onClick={onClose}>
                  <Icon name="X" size={20} />
                </Button>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="p-4 border-b border-border bg-muted/30">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-card border border-border rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Net Salary</p>
                <p className="text-lg font-semibold text-foreground">{formatCurrency(netSalary || 0)}</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Total Paid</p>
                <p className="text-lg font-semibold text-success">{formatCurrency(totalPaid)}</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Remaining</p>
                <p className={`text-lg font-semibold ${remaining > 0 ? 'text-warning' : remaining < 0 ? 'text-destructive' : 'text-success'}`}>
                  {formatCurrency(remaining)}
                </p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-4">
              <Select
                value={filterType}
                onValueChange={setFilterType}
                options={[
                  { value: 'all', label: 'All Types' },
                  ...paymentTypes
                ]}
                className="w-48"
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <NotificationToast
              message={notification?.message}
              type={notification?.type}
              onDismiss={() => setNotification(null)}
            />

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading payments...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-destructive">{error}</p>
                <Button variant="outline" onClick={loadPayments} className="mt-4">
                  Retry
                </Button>
              </div>
            ) : filteredPayments.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="CreditCard" size={48} className="mx-auto mb-4 opacity-50 text-muted-foreground" />
                <p className="text-muted-foreground">No payments found</p>
                <Button variant="outline" onClick={handleAdd} className="mt-4">
                  Add First Payment
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredPayments.map((payment) => (
                  <div
                    key={payment.id}
                    className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            payment.payment_type === 'salary' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                            payment.payment_type === 'advance' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
                            payment.payment_type === 'bonus' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                            payment.payment_type === 'reimbursement' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                          }`}>
                            {paymentTypes.find(t => t.value === payment.payment_type)?.label || payment.payment_type}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {new Date(payment.payment_date).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                        <p className="text-lg font-semibold text-foreground">{formatCurrency(payment.amount)}</p>
                        {payment.notes && (
                          <p className="text-sm text-muted-foreground mt-1">{payment.notes}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(payment)}
                          title="Edit"
                        >
                          <Icon name="Edit" size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(payment.id)}
                          className="text-destructive"
                          title="Delete"
                        >
                          <Icon name="Trash2" size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <PaymentFormModal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setEditingPayment(null);
          }}
          payment={editingPayment}
          onSave={handleSave}
          paymentTypes={paymentTypes}
          netSalary={netSalary}
          totalPaid={totalPaid}
        />
      )}
    </>
  );
};

// Payment Form Modal Component
const PaymentFormModal = ({ isOpen, onClose, payment, onSave, paymentTypes, netSalary, totalPaid }) => {
  const [formData, setFormData] = useState({
    payment_type: payment?.payment_type || 'salary',
    payment_date: payment?.payment_date || new Date().toISOString().split('T')[0],
    amount: payment?.amount || '',
    notes: payment?.notes || ''
  });
  const [error, setError] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.payment_type || !formData.payment_date || !formData.amount) {
      setError('Please fill in all required fields');
      return;
    }

    if (parseFloat(formData.amount) <= 0) {
      setError('Amount must be greater than 0');
      return;
    }

    // Check if total payments exceed net salary (only for new payments)
    if (!payment) {
      const newTotal = totalPaid + parseFloat(formData.amount);
      if (netSalary && newTotal > netSalary * 1.1) { // Allow 10% tolerance
        if (!window.confirm(`Total payments (${newTotal.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}) will exceed net salary (${netSalary.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}). Continue?`)) {
          return;
        }
      }
    }

    onSave({
      payment_type: formData.payment_type,
      payment_date: formData.payment_date,
      amount: parseFloat(formData.amount),
      notes: formData.notes || null
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-background rounded-lg w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-foreground">
              {payment ? 'Edit Payment' : 'Add Payment'}
            </h3>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Payment Type *
            </label>
            <Select
              value={formData.payment_type}
              onValueChange={(value) => setFormData({ ...formData, payment_type: value })}
              options={paymentTypes}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Payment Date *
            </label>
            <Input
              type="date"
              value={formData.payment_date}
              onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Amount (₹) *
            </label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              placeholder="0.00"
              required
            />
            {netSalary && !payment && (
              <p className="text-xs text-muted-foreground mt-1">
                Net Salary: ₹{netSalary.toLocaleString('en-IN')} | 
                Already Paid: ₹{totalPaid.toLocaleString('en-IN')} | 
                Remaining: ₹{(netSalary - totalPaid).toLocaleString('en-IN')}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Notes
            </label>
            <textarea
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              rows="3"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Payment details, transaction reference, etc."
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-4">
            <Button variant="outline" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {payment ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentsBreakdownModal;









